import React, { useState, useEffect, useCallback } from "react";
import RefreshIcon from "../../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../../core/common/tooltip-content/collapes";
import CommonFooter from "../../../core/common/footer/commonFooter";
import useStore from "../../../store/store";
import StoreServices from "../../../services/StoreServices";
import { getStoreLogoUrl } from "../../../utils/imageUtils";

const GeneralSettings = () => {
  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  // Form state for editable fields
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    gstAvailable: false,
    gstNumber: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [currentLogoUrl, setCurrentLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch existing logo
  const fetchExistingLogo = useCallback(async () => {
    if (!storeId || !selectedStore?.logo) return;

    try {
      const logoInfo = getStoreLogoUrl(selectedStore, "assets/img/logo.png");
      setCurrentLogoUrl(logoInfo.url);
    } catch (error) {
      // Logo might not exist, which is fine
      console.log("No existing logo found");
    }
  }, [storeId, selectedStore]);

  // Initialize form data when selectedStore changes
  useEffect(() => {
    if (selectedStore) {
      setFormData({
        name: selectedStore.name || "",
        email: selectedStore.email || "",
        phone: selectedStore.phone || "",
        address: selectedStore.address || "",
        gstAvailable: selectedStore.gstAvailable || false,
        gstNumber: selectedStore.gstNumber || "",
      });

      // Clear previous logo states when switching stores
      setLogoFile(null);
      setLogoPreview("");
      setCurrentLogoUrl("");
      setLogoError("");

      // Fetch existing logo if available
      fetchExistingLogo();
    }
  }, [selectedStore, fetchExistingLogo]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoError("");

    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setLogoError("Please select an image file");
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setLogoError("File size must be less than 5MB");
        return;
      }

      setLogoFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const [updating, setUpdating] = useState(false);

  // Manual update store function
  const updateStore = async () => {
    if (!storeId) {
      throw new Error("No store selected");
    }
    setUpdating(true);
    try {
      let response;

      // If logo file is selected, use the multipart upload endpoint
      if (logoFile) {
        response = await StoreServices.updateStoreWithLogo(
          storeId,
          formData,
          logoFile
        );
      } else {
        // Use regular update endpoint
        response = await StoreServices.updateStore(storeId, formData);
      }

      return response;
    } finally {
      setUpdating(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLogoError("");

    try {
      await updateStore();
      setSuccess("Store settings updated successfully!");

      // Reset logo file and preview
      setLogoFile(null);
      setLogoPreview("");

      // Fetch updated logo URL
      if (logoFile) {
        await fetchExistingLogo();
      }

      // Optionally reload the page or update the store in Zustand
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to update store settings");
    }
  };
  return (
    <div>
      <div className="page-wrapper">
        <div className="content settings-content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">Settings</h4>
                <h6>Manage your settings on portal</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <RefreshIcon />
              <CollapesIcon />
            </ul>
          </div>
          <div className="row">
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                {/* <SettingsSideBar /> */}
                <div className="card flex-fill mb-0">
                  <div className="card-header">
                    <h4 className="fs-18 fw-bold">Profile</h4>
                  </div>
                  <div className="card-body">
                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="alert alert-success" role="alert">
                        {success}
                      </div>
                    )}
                    {logoError && logoFile && (
                      <div className="alert alert-warning" role="alert">
                        {logoError}
                      </div>
                    )}
                    <form onSubmit={handleSubmit}>
                      <div className="card-title-head">
                        <h6 className="fs-16 fw-bold mb-3">
                          <span className="fs-16 me-2">
                            <i className="ti ti-user" />
                          </span>
                          Basic Information
                        </h6>
                      </div>
                      <div className="profile-pic-upload">
                        <div className="profile-pic">
                          {(() => {
                            if (logoPreview) {
                              return (
                                <div className="uploaded-img">
                                  <img
                                    src={logoPreview}
                                    alt="Store Logo Preview"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                  />
                                </div>
                              );
                            }

                            if (currentLogoUrl && !logoError) {
                              return (
                                <div className="uploaded-img">
                                  <img
                                    src={currentLogoUrl}
                                    alt="Store Logo"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                    onError={() => setLogoError(true)}
                                  />
                                </div>
                              );
                            }

                            return (
                              <span>
                                <i className="ti ti-circle-plus mb-1 fs-16" />{" "}
                                Add Logo
                              </span>
                            );
                          })()}
                        </div>
                        <div className="new-employee-field">
                          <div className="mb-0">
                            <div className="image-upload mb-0">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                              />
                              <div className="image-uploads">
                                <h4>
                                  {logoPreview || currentLogoUrl
                                    ? "Change Logo"
                                    : "Upload Logo"}
                                </h4>
                              </div>
                            </div>
                            <span className="fs-13 fw-medium mt-2">
                              Upload an image below 5 MB, Accepted File format
                              JPG, PNG, GIF
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Store Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Phone Number{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <div className="localization-select d-flex align-items-center">
                            <label className="form-label me-3">
                              GST Available
                            </label>
                            <div className="status-toggle modal-status d-flex justify-content-between align-items-center me-3">
                              <input
                                type="checkbox"
                                id="gstToggle"
                                className="check"
                                name="gstAvailable"
                                checked={formData.gstAvailable}
                                onChange={handleChange}
                              />
                              <label
                                htmlFor="gstToggle"
                                className="checktoggle"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {formData.gstAvailable && (
                        <div className="row mb-3">
                          <div className="col-md-4">
                            <div className="mb-3">
                              <label className="form-label">
                                GST Number{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="gstNumber"
                                placeholder="Enter GST Number"
                                value={formData.gstNumber}
                                onChange={handleChange}
                                required={formData.gstAvailable}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="card-title-head">
                        <h6 className="fs-16 fw-bold mb-3">
                          <span className="fs-16 me-2">
                            <i className="ti ti-map-pin" />
                          </span>
                          Address Information
                        </h6>
                      </div>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label className="form-label">
                              Full Address{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="address"
                              value={formData.address}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-end settings-bottom-btn mt-0">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={updating}
                        >
                          {updating ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default GeneralSettings;
